import { useState, useMemo } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import React from "react";

// Register ChartJS components once outside component
ChartJS.register(ArcElement, Tooltip, Legend);

interface SalesData {
  dineIn: { orders: number };
  takeaway: { orders: number };
  partyOrders: { orders: number };
  menuOrders: { orders: number };
}

interface SalesChartProps {
  salesData: Record<string, SalesData>;
  isDarkMode: boolean;
}

// Memoized component
export const SalesChart = React.memo(function SalesChart({ salesData, isDarkMode }: SalesChartProps) {
  const [timeFilter, setTimeFilter] = useState<string>("daily");

  // Memoized current data
  const currentData = useMemo(
    () => salesData[timeFilter] || {
      dineIn: { orders: 0 },
      takeaway: { orders: 0 },
      partyOrders: { orders: 0 },
      menuOrders: { orders: 0 },
    },
    [salesData, timeFilter]
  );

  // Memoized pie chart data
  const pieChartData = useMemo(() => ({
    labels: ["Dine In", "Takeaway", "Party Orders", "Menu Orders"],
    datasets: [{
      data: [
        currentData.dineIn.orders,
        currentData.takeaway.orders,
        currentData.partyOrders.orders,
        currentData.menuOrders.orders,
      ],
      backgroundColor: ["#059669", "#10b981", "#34d399", "#6ee7b7"],
      borderColor: isDarkMode ? "#111827" : "#f9fafb",
      borderWidth: 2,
    }],
  }), [currentData, isDarkMode]);

  // Memoized options
  const options = useMemo(() => ({
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: isDarkMode ? "#d1d5db" : "#6b7280",
          font: { size: 14, family: "'font-display', sans-serif" },
        },
        position: "bottom" as const,
      },
      tooltip: {
        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
        titleColor: isDarkMode ? "#e5e7eb" : "#111827",
        bodyColor: isDarkMode ? "#e5e7eb" : "#111827",
      },
    },
  }), [isDarkMode]);

  // Memoized filters array
  const filters = useMemo(() => ["daily", "weekly", "monthly", "yearly", "alltime"], []);

  return (
    <div
      className={`max-w-7xl mx-auto rounded-lg shadow-lg p-4 sm:p-6 ${
        isDarkMode ? "bg-gray-900" : "bg-gray-50"
      }`}
    >
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
        <h3
          className={`font-display text-xl sm:text-2xl font-bold ${
            isDarkMode ? "text-white" : "text-gray-900"
          } mb-2 sm:mb-0`}
        >
          Sales Distribution
        </h3>
        <div className="flex flex-wrap gap-2 justify-center">
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setTimeFilter(filter)}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                timeFilter === filter
                  ? "bg-emerald-600 text-white shadow-md"
                  : `${
                      isDarkMode
                        ? "bg-gray-800 text-gray-400 hover:bg-gray-700"
                        : "bg-white text-gray-600 hover:bg-gray-100"
                    } border border-gray-300 dark:border-gray-700`
              }`}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-[250px] sm:h-[300px] flex items-center justify-center">
        <Pie data={pieChartData} options={options} />
      </div>
    </div>
  );
});